import { captureException } from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { AiChatReplyStatus } from '../../../server/utils/ai-chat-helper.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { pressMessageRating, type AskPlatoMessageRating } from '../types/message-rating.ts';

export type { AiChatReplyStatus, AskPlatoMessageRating };

export type AskPlatoChatMessage = {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    messageIndex: number;
    rating?: AskPlatoMessageRating;
    /**
     * The outcome the server assigned this reply, set from the terminal chunk.
     * Absent while streaming, and on replies whose stream died before the
     * server could report on it — those surface through `hasError` instead.
     */
    status?: AiChatReplyStatus;
};

export type AskPlatoChat = {
    messages: AskPlatoChatMessage[];
    isStreaming: boolean;
    hasError: boolean;
    /**
     * TEMPORARY (testing aid): raw description of what the assistant is doing
     * right now ("query_business_data: executing_query"). The real UI should
     * replace this with translated, designed tool-activity states.
     */
    activity: string | undefined;
    sendMessage: (message: string) => void;
    toggleRating: (messageId: string, rating: AskPlatoMessageRating) => void;
};

export const useAskPlatoChat = (): AskPlatoChat => {
    const sendEvent = useAnalytics();
    const [messages, setMessages] = useState<AskPlatoChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [activity, setActivity] = useState<string | undefined>(undefined);
    const conversationIdRef = useRef<string | undefined>(undefined);
    const messageIndexRef = useRef(0);
    const streamingRef = useRef(false);

    const { mutateAsync: createConversation } = api.platoAiChat.conversations.create.useMutation();
    const { mutateAsync: sendChatMessage } = api.platoAiChat.conversations.sendMessage.useMutation();
    const { mutateAsync: generateTitle } = api.platoAiChat.conversations.generateTitle.useMutation();

    const sendMessage = useCallback(
        (message: string) => {
            const trimmedMessage = message.trim();
            if (trimmedMessage.length === 0 || streamingRef.current) {
                return;
            }

            streamingRef.current = true;
            setIsStreaming(true);
            setHasError(false);

            const messageIndex = messageIndexRef.current;
            messageIndexRef.current += 1;
            sendEvent('ai_chat_message_submitted', {
                conversation_id: conversationIdRef.current,
                message_index: messageIndex,
                message_length_chars: trimmedMessage.length,
                message_text: trimmedMessage,
            });

            const assistantMessageId = uuidv4();
            setMessages((previous) => [
                ...previous,
                { id: uuidv4(), role: 'user', text: trimmedMessage, messageIndex },
                { id: assistantMessageId, role: 'assistant', text: '', messageIndex },
            ]);

            const run = async (): Promise<void> => {
                let conversationId = conversationIdRef.current;
                const isFirstMessage = conversationId === undefined;

                if (conversationId === undefined) {
                    ({ conversationId } = await createConversation());
                    conversationIdRef.current = conversationId;
                }

                // The title is derived from the opening message only, and the
                // reply must not wait on it — the server persists it itself, so
                // fire it alongside the stream and let a failure stay silent.
                if (isFirstMessage) {
                    void generateTitle({ conversationId, message: trimmedMessage }).catch(
                        (error: unknown) => {
                            captureException(error);
                        },
                    );
                }

                const chunks = await sendChatMessage({ conversationId, message: trimmedMessage });

                for await (const chunk of chunks) {
                    if (chunk.type === 'delta') {
                        // Text is flowing again — whatever tool was running is done.
                        setActivity(undefined);
                        setMessages((previous) =>
                            previous.map((item) =>
                                item.id === assistantMessageId
                                    ? { ...item, text: item.text + chunk.text }
                                    : item,
                            ),
                        );
                    }
                    // Tool chunks carry display-ready copy; show it as-is.
                    if (chunk.type === 'toolCall' || chunk.type === 'toolStatus') {
                        setActivity(chunk.text);
                    }
                    // The terminal chunk. On the server's error path it is
                    // flushed before the throw, so the outcome is reported for
                    // failed replies too — the loop rejects on the next pull.
                    if (chunk.type === 'done') {
                        const { status } = chunk;
                        setMessages((previous) =>
                            previous.map((item) =>
                                item.id === assistantMessageId ? { ...item, status } : item,
                            ),
                        );
                        sendEvent('ai_chat_reply_shown', {
                            status: chunk.status,
                            conversation_id: chunk.conversationId,
                            message_index: messageIndex,
                            message_id: chunk.messageId,
                        });
                    }
                }
            };

            run()
                .catch((error: unknown) => {
                    captureException(error);
                    setHasError(true);
                    setMessages((previous) =>
                        previous.filter((item) => item.id !== assistantMessageId || item.text.length > 0),
                    );
                })
                .finally(() => {
                    streamingRef.current = false;
                    setIsStreaming(false);
                    setActivity(undefined);
                });
        },
        [createConversation, sendChatMessage, generateTitle, sendEvent],
    );

    const toggleRating = useCallback(
        (messageId: string, rating: AskPlatoMessageRating) => {
            const message = messages.find((item) => item.id === messageId);
            if (message === undefined || message.role !== 'assistant') {
                return;
            }

            const press = pressMessageRating(message.rating, rating);

            setMessages((previous) =>
                previous.map((item) => (item.id === messageId ? { ...item, rating: press.rating } : item)),
            );

            sendEvent('ai_chat_message_rated', {
                conversation_id: conversationIdRef.current,
                message_index: message.messageIndex,
                rating: press.reported,
                response_length_chars: message.text.length,
            });
        },
        [messages, sendEvent],
    );

    return { messages, isStreaming, hasError, activity, sendMessage, toggleRating };
};
